import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, parse } from "date-fns";
import { toast } from "sonner";
import { 
  Settings, LogOut, QrCode, Calendar, Users, DollarSign, 
  Bell, ChevronRight, Utensils, AlertTriangle, Compass, Home,
  CheckCircle, XCircle, Loader2
} from "lucide-react";
import { getAdminStats, getCheckoutNotifications, getAdminTourReservations, updateTourReservation } from "@/lib/api";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [tourReservations, setTourReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingTour, setConfirmingTour] = useState(null);

  useEffect(() => {
    // Check auth
    if (sessionStorage.getItem("adminAuth") !== "true") {
      navigate("/admin");
      return;
    }

    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, notifRes, tourRes] = await Promise.all([
        getAdminStats(),
        getCheckoutNotifications(),
        getAdminTourReservations({ status: "pending" })
      ]);
      setStats(statsRes.data);
      setNotifications(notifRes.data);
      setTourReservations(tourRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmTour = async (reservation) => {
    try {
      setConfirmingTour(reservation.id);
      await updateTourReservation(reservation.id, {
        status: "confirmed",
        admin_notes: "Confirmed from dashboard"
      });
      toast.success(`${reservation.tour_name} confirmed for ${reservation.guest_name}`);
      loadData();
    } catch (error) {
      console.error("Error confirming tour:", error);
      toast.error("Failed to confirm tour");
    } finally {
      setConfirmingTour(null);
    }
  };

  const handleRejectTour = async (reservation) => {
    try {
      setConfirmingTour(reservation.id);
      await updateTourReservation(reservation.id, {
        status: "rejected",
        admin_notes: "Rejected from dashboard"
      });
      toast.success(`Tour reservation rejected`);
      loadData();
    } catch (error) {
      console.error("Error rejecting tour:", error);
      toast.error("Failed to reject tour");
    } finally {
      setConfirmingTour(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Settings className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-xl font-bold">Admin Dashboard</h1>
                <p className="text-sm text-muted-foreground">La Olla</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-card/50 border-b border-border">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto py-2">
            <Link to="/admin/dashboard">
              <Button variant="ghost" className="bg-primary/10 text-primary" data-testid="nav-dashboard">
                <Utensils className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <Link to="/admin/bird-brain-guests">
              <Button variant="ghost" data-testid="nav-bbb-guests">
                <Home className="w-4 h-4 mr-2" />
                BBB Guests
              </Button>
            </Link>
            <Link to="/admin/reservations">
              <Button variant="ghost" data-testid="nav-reservations">
                <Calendar className="w-4 h-4 mr-2" />
                Reservations
              </Button>
            </Link>
            <Link to="/admin/tours">
              <Button variant="ghost" data-testid="nav-tours">
                <Compass className="w-4 h-4 mr-2" />
                Tours
              </Button>
            </Link>
            <Link to="/admin/qr-codes">
              <Button variant="ghost" data-testid="nav-qr-codes">
                <QrCode className="w-4 h-4 mr-2" />
                QR Codes
              </Button>
            </Link>
            <Link to="/admin/calendars">
              <Button variant="ghost" data-testid="nav-calendars">
                <Calendar className="w-4 h-4 mr-2" />
                Calendars
              </Button>
            </Link>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card border-border" data-testid="stat-reservations">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Today's Reservations</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.today_reservations || 0}</p>
                </div>
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="stat-guests">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Today's Guests</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.today_guests || 0}</p>
                </div>
                <div className="w-12 h-12 bg-info/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-info" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="stat-revenue">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Today's Revenue</p>
                  <p className="text-3xl font-bold text-foreground">${(stats?.today_revenue || 0).toFixed(2)}</p>
                </div>
                <div className="w-12 h-12 bg-success/20 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-success" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="stat-checkouts">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Tomorrow's Checkouts</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.tomorrow_checkouts || 0}</p>
                </div>
                <div className="w-12 h-12 bg-warning/20 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-warning" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Checkout Notifications */}
        <Card className="bg-card border-border" data-testid="checkout-notifications">
          <CardHeader>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              Checkout Notifications
            </CardTitle>
            <CardDescription>
              Guests checking out tomorrow - charge their accounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No checkout notifications for tomorrow</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {notifications.map((notif) => (
                    <div
                      key={notif.guest_id}
                      className="bg-background/50 border border-border rounded-lg p-4"
                      data-testid={`checkout-notif-${notif.guest_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-lg">{notif.guest_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {notif.hotel_name} • Cabin {notif.cabin_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Checkout: {format(parse(notif.departure_date, "yyyy-MM-dd", new Date()), "MMMM d, yyyy")}
                          </p>
                        </div>
                        <Badge className="bg-primary text-primary-foreground text-lg px-3 py-1">
                          ${notif.total_amount.toFixed(2)}
                        </Badge>
                      </div>
                      
                      {notif.reservations.length > 0 && (
                        <div className="border-t border-border pt-3">
                          <p className="text-sm text-muted-foreground mb-2">Reservations:</p>
                          <div className="space-y-1">
                            {notif.reservations.map((res) => (
                              <div key={res.id} className="flex justify-between text-sm">
                                <span>
                                  {res.meal_name} - {format(parse(res.date, "yyyy-MM-dd", new Date()), "MMM d")}
                                  {res.room_service && " (Room Service)"}
                                </span>
                                <span className="text-muted-foreground">
                                  {res.guest_count} guests • ${res.price.toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Pending Tour Reservations */}
        <Card className="bg-card border-border mt-6" data-testid="pending-tours">
          <CardHeader>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Compass className="w-5 h-5 text-blue-600" />
              Pending Tour Reservations
              {tourReservations.length > 0 && (
                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 ml-2">
                  {tourReservations.length}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Tour bookings awaiting confirmation
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tourReservations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Compass className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No pending tour reservations</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {tourReservations.map((res) => (
                    <div
                      key={res.id}
                      className="bg-background/50 border border-border rounded-lg p-4"
                      data-testid={`tour-res-${res.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-lg">{res.tour_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {res.guest_name} • {res.cabin_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Date: {format(parse(res.date, "yyyy-MM-dd", new Date()), "MMMM d, yyyy")}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {res.num_participants} participant{res.num_participants > 1 ? 's' : ''} • ${res.total_price?.toFixed(2) || res.num_participants * 100}
                          </p>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                          Pending
                        </Badge>
                      </div>
                      
                      {res.deposit_required && (
                        <p className="text-sm mb-3">
                          <span className="text-muted-foreground">Deposit:</span>{" "}
                          <span className={res.deposit_confirmed ? "text-green-600" : "text-orange-600"}>
                            ${res.deposit_amount?.toFixed(2)} {res.deposit_confirmed ? "✓ Confirmed" : "⏳ Pending"}
                          </span>
                        </p>
                      )}

                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => handleConfirmTour(res)}
                          disabled={confirmingTour === res.id}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`confirm-tour-${res.id}`}
                        >
                          {confirmingTour === res.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-1" />
                          )}
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRejectTour(res)}
                          disabled={confirmingTour === res.id}
                          data-testid={`reject-tour-${res.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Link to="/admin/tours">
                          <Button size="sm" variant="outline">
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <Link to="/admin/reservations">
            <Card className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer card-interactive">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-8 h-8 text-primary" />
                    <div>
                      <p className="font-semibold">Reservations</p>
                      <p className="text-sm text-muted-foreground">Manage meals</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/tours">
            <Card className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer card-interactive">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Compass className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="font-semibold">Tours</p>
                      <p className="text-sm text-muted-foreground">Bookings & availability</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/bird-brain-guests">
            <Card className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer card-interactive">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Home className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-semibold">BBB Guests</p>
                      <p className="text-sm text-muted-foreground">Arrivals & details</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/qr-codes">
            <Card className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer card-interactive">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <QrCode className="w-8 h-8 text-primary" />
                    <div>
                      <p className="font-semibold">QR Codes</p>
                      <p className="text-sm text-muted-foreground">Print for cabins</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </main>
    </div>
  );
}
